import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatVND } from "@/lib/api";
import { getSourceLabel, getTagLabel } from "@/lib/labels";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Phone, Mail, MapPin, Calendar, DollarSign,
  Tag, Clock, Send, Edit, Trash2, User,
} from "lucide-react";
import PinDialog from "@/components/PinDialog";
import CustomerModal from "@/components/customers/CustomerModal";

const STATUS_MAP = {
  confirmed: { label: "Xác nhận", cls: "bg-lime-500/20 text-lime-400 border-lime-500/30" },
  pending: { label: "Chờ", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  completed: { label: "Hoàn thành", cls: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  cancelled: { label: "Đã hủy", cls: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const TAG_COLORS = {
  VIP: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Khách quen": "bg-lime-500/20 text-lime-400 border-lime-500/30",
  "Thuê tháng": "bg-sky-500/20 text-sky-400 border-sky-500/30",
  "Nhóm": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  Social: "bg-teal-500/20 text-teal-400 border-teal-500/30",
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [custRes, bookRes, noteRes] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/bookings`),
        api.get(`/customers/${id}/notes`),
      ]);
      setCustomer(custRes.data.customer);
      setBookings(bookRes.data.bookings || []);
      setNotes(noteRes.data.notes || []);
    } catch (err) {
      toast.error("Không thể tải thông tin khách hàng");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSendingNote(true);
    try {
      const { data } = await api.post(`/customers/${id}/notes`, { content: noteText.trim() });
      setNotes(prev => [data.note, ...prev]);
      setNoteText("");
      toast.success("Đã thêm ghi chú");
    } catch (err) {
      toast.error("Lỗi khi thêm ghi chú");
    } finally {
      setSendingNote(false);
    }
  };

  const handleDelete = () => {
    setPinOpen(true);
  };
  const doDelete = async () => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Đã xóa khách hàng");
      navigate("/customers");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi khi xóa");
    }
  };
  const [pinOpen, setPinOpen] = useState(false);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="customer-detail-loading">
        <div className="h-8 w-48 bg-[#334155] rounded" />
        <div className="h-40 bg-[#1e293b] rounded-lg" />
      </div>
    );
  }

  if (!customer) return null;

  const stats = customer.stats || {};
  const tabs = [
    { key: "overview", label: "Tổng quan" },
    { key: "bookings", label: `Lịch sử đặt sân (${bookings.length})` },
    { key: "notes", label: `Ghi chú (${notes.length})` },
  ];

  const activeBookings = bookings.filter(b => b.status !== "cancelled");
  const cancelledBookings = bookings.filter(b => b.status === "cancelled");

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="customer-detail-page">
      {/* Back + Actions */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/customers")} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 transition-colors text-sm" data-testid="back-to-customers">
          <ArrowLeft className="w-4 h-4" />
          Danh sách khách hàng
        </button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-[#334155] text-slate-300 gap-1" onClick={() => setEditOpen(true)} data-testid="edit-customer-detail-btn">
            <Edit className="w-3.5 h-3.5" /> Sửa
          </Button>
          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 gap-1" onClick={handleDelete} data-testid="delete-customer-btn">
            <Trash2 className="w-3.5 h-3.5" /> Xóa
          </Button>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-5">
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="w-16 h-16 rounded-full bg-[#334155] flex items-center justify-center text-2xl font-bold text-lime-400 shrink-0">
            {customer.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <h2 className="font-heading text-xl font-bold text-[#F8FAFC]">{customer.name}</h2>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(customer.tags || []).map(tag => (
                  <Badge key={tag} className={`${TAG_COLORS[tag] || TAG_COLORS[getTagLabel(tag)] || "bg-slate-500/20 text-slate-400 border-slate-500/30"} text-[10px] px-1.5 py-0 border`}>
                    {getTagLabel(tag)}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Phone className="w-3.5 h-3.5 text-slate-500" />
                {customer.phone || "\u2014"}
                {customer.phone_secondary && <span className="text-slate-500">/ {customer.phone_secondary}</span>}
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Mail className="w-3.5 h-3.5 text-slate-500" />
                {customer.email || "\u2014"}
              </div>
              {customer.address && (
                <div className="flex items-center gap-2 text-slate-300">
                  <MapPin className="w-3.5 h-3.5 text-slate-500" />
                  {customer.address}
                </div>
              )}
              {customer.source && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Tag className="w-3.5 h-3.5 text-slate-500" />
                  Nguồn: {getSourceLabel(customer.source)}
                </div>
              )}
              {customer.gender && (
                <div className="flex items-center gap-2 text-slate-300">
                  <User className="w-3.5 h-3.5 text-slate-500" />
                  {customer.gender}
                </div>
              )}
              {customer.birthday && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  Sinh: {customer.birthday}
                </div>
              )}
              {customer.region && (
                <div className="flex items-center gap-2 text-slate-300">
                  <MapPin className="w-3.5 h-3.5 text-slate-500" />
                  KV: {customer.region}{customer.ward ? ` / ${customer.ward}` : ""}
                </div>
              )}
              {customer.kv_code && (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  Mã KV: {customer.kv_code}
                </div>
              )}
              {customer.company && (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  Cty: {customer.company}
                </div>
              )}
              {customer.customer_group && (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  Nhóm: {customer.customer_group}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-[#334155]">
          <div className="text-center">
            <div className="text-2xl font-bold text-lime-400">{stats.total_bookings || 0}</div>
            <div className="text-xs text-slate-500">Lượt đặt sân</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-400">{formatVND(stats.total_spent || 0)}đ</div>
            <div className="text-xs text-slate-500">Tổng chi tiêu</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-sky-400">
              {stats.last_booking_date
                ? new Date(stats.last_booking_date + "T00:00:00").toLocaleDateString("vi-VN")
                : "\u2014"}
            </div>
            <div className="text-xs text-slate-500">Lần cuối đặt</div>
          </div>
        </div>
        {/* KV Coffee stats */}
        {(customer.kv_total_sales > 0 || customer.kv_total_points > 0) && (
          <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-[#334155]/50">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-400">{formatVND(customer.kv_total_sales || 0)}đ</div>
              <div className="text-[10px] text-slate-500">Tổng bán (KV)</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-cyan-400">{Math.round(customer.kv_total_points || 0)}</div>
              <div className="text-[10px] text-slate-500">Tổng điểm (KV)</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-red-400">{formatVND(customer.kv_debt || 0)}đ</div>
              <div className="text-[10px] text-slate-500">Nợ cần thu</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-1" data-testid="customer-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "bg-[#1e293b] text-lime-400"
                : "text-slate-400 hover:text-slate-200"
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="space-y-4" data-testid="tab-content-overview">
          {/* Notes preview */}
          {customer.notes && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-2">Ghi chú chung</h3>
              <p className="text-sm text-slate-400">{customer.notes}</p>
            </div>
          )}
          {/* Recent bookings preview */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Đặt sân gần đây</h3>
            {activeBookings.length === 0 ? (
              <p className="text-sm text-slate-500">Chưa có đặt sân nào</p>
            ) : (
              <div className="space-y-2">
                {activeBookings.slice(0, 5).map(b => (
                  <div key={b.id} className="flex items-center justify-between py-2 border-b border-[#334155]/50 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-slate-500 w-20">{b.date ? new Date(b.date + "T00:00:00").toLocaleDateString("vi-VN") : ""}</div>
                      <span className="text-sm text-slate-300">{b.court_name}</span>
                      <span className="text-xs text-slate-500">{b.start_time}-{b.end_time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${STATUS_MAP[b.status]?.cls || ""} text-[10px] px-1.5 py-0 border`}>
                        {STATUS_MAP[b.status]?.label || b.status}
                      </Badge>
                      <span className="text-sm text-lime-400 font-medium">{formatVND(b.total_price || 0)}đ</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Customer created info */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <User className="w-3.5 h-3.5" />
              Khách hàng từ {customer.created_at ? new Date(customer.created_at).toLocaleDateString("vi-VN") : "\u2014"}
            </div>
          </div>
        </div>
      )}

      {activeTab === "bookings" && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden" data-testid="tab-content-bookings">
          <Table>
            <TableHeader>
              <TableRow className="border-[#334155] hover:bg-transparent">
                <TableHead className="text-slate-400">Ngày</TableHead>
                <TableHead className="text-slate-400">Sân</TableHead>
                <TableHead className="text-slate-400">Giờ</TableHead>
                <TableHead className="text-slate-400">Loại</TableHead>
                <TableHead className="text-slate-400">Trạng thái</TableHead>
                <TableHead className="text-slate-400">Giá</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow className="border-[#334155]">
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Chưa có đặt sân nào
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map(b => (
                  <TableRow key={b.id} className="border-[#334155] hover:bg-[#334155]/20" data-testid={`booking-row-${b.id}`}>
                    <TableCell className="text-slate-300 text-sm">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-slate-500" />
                        {b.date ? new Date(b.date + "T00:00:00").toLocaleDateString("vi-VN") : "\u2014"}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">{b.court_name}</TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        {b.start_time}-{b.end_time}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${b.booking_type === "social" ? "bg-teal-500/20 text-teal-400 border-teal-500/30" : "bg-slate-500/20 text-slate-400 border-slate-500/30"} text-[10px] px-1.5 py-0 border`}>
                        {b.booking_type === "social" ? "Social" : "Thường"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_MAP[b.status]?.cls || ""} text-[10px] px-1.5 py-0 border`}>
                        {STATUS_MAP[b.status]?.label || b.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <DollarSign className="w-3.5 h-3.5 text-lime-500" />
                        <span className="text-lime-400 font-medium">{formatVND(b.total_price || 0)}đ</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {activeTab === "notes" && (
        <div className="space-y-4" data-testid="tab-content-notes">
          {/* Add note form */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Thêm ghi chú mới..."
              className="bg-[#0f172a] border-[#334155] text-slate-200 min-h-[80px] mb-3"
              data-testid="note-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleAddNote}
                disabled={!noteText.trim() || sendingNote}
                className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold gap-1.5"
                data-testid="add-note-btn"
              >
                <Send className="w-3.5 h-3.5" />
                {sendingNote ? "Đang gửi..." : "Thêm ghi chú"}
              </Button>
            </div>
          </div>

          {/* Notes list */}
          {notes.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Chưa có ghi chú nào</div>
          ) : (
            <div className="space-y-3">
              {notes.map(note => (
                <div key={note.id} className="bg-[#1e293b] border border-[#334155] rounded-lg p-4" data-testid={`note-${note.id}`}>
                  <p className="text-sm text-slate-200 whitespace-pre-wrap">{note.content}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                    <span>{note.created_by_name}</span>
                    <span>&middot;</span>
                    <span>{note.created_at ? new Date(note.created_at).toLocaleString("vi-VN") : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CustomerModal
        open={editOpen}
        onOpenChange={setEditOpen}
        customer={customer}
        allTags={customer.tags}
        allSources={[]}
        onSaved={loadData}
      />
      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={doDelete} title="Xác nhận xóa khách hàng" description="Nhập mã PIN để xóa khách hàng này." />
    </div>
  );
}
